import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  FormProvider,
  useForm,
} from "react-hook-form";
import qs from "qs";

import { Button } from "../../components/Button/Button";
import { readProfile, StyleProfile } from "../ShowStylePage/profile";
import { buildGuidelinesPayload } from "./guidelinesPayload";
import { htmlToPlainText, plainTextToHtml } from "./textFormat";
import {
  SourceSection,
  DescriptionSection,
  GuidelinesSection,
  ParamsSection,
  ProfileSection,
  EntryFieldsSection,
  PreviewSection,
  SidebarWidgets,
} from "./components";

interface StyleFormData {
  name: string;
  description: string;
  drinkType: "beer" | "cider" | "mead" | "wine";
  scoresheetSchemaId: string;
  imageUrl?: string;
  baseStyle?: string;
  version?: string;
  documentPath?: string;
  documentName?: string;
  guidelines?: any;
  // Guidelines values
  minColor?: string;
  maxColor?: string;
  minOg?: string;
  maxOg?: string;
  minFg?: string;
  maxFg?: string;
  minAbv?: string;
  maxAbv?: string;
  minIbu?: string;
  maxIbu?: string;
  minServingTemp?: string;
  maxServingTemp?: string;
  // Sensory profile: a range per trait, null when not set
  profile: StyleProfile;
  // Additional fields
  settings: {
    entryAdditionalFields: any[];
  };
  [key: string]: any;
}

interface StyleCreatorPageProps {
  mode?: "create" | "edit";
  onStyleSubmit: (values: any) => void;
  onCancel?: () => void;
  useApiClient: () => {
    styles: {
      findStyle: (params: any) => Promise<any>;
      useFindStyle: (params: any, options?: any) => { data: any };
      useCreateStyle: () => { mutateAsync: (values: any) => Promise<any>; mutate: (values: any, options: any) => void };
      useUpdateStyle: () => { mutateAsync: (values: any) => Promise<any>; mutate: (values: any, options: any) => void };
      useGenerateGuidelines: () => { mutateAsync: (payload: { id: string }) => Promise<any>; isPending: boolean };
    };
    scoresheetSchemas: {
      useListScoresheetSchemas: (params: any) => { data: any };
      useFindScoresheetSchema?: (params: any, options?: any) => { data: any };
    };
  };
  AdditionalFieldsComponent?: React.ComponentType<any>;
  showNotification?: (notification: {
    type: string;
    title: string;
    message: string;
  }) => void;
}

export const StyleCreatorPage: React.FC<StyleCreatorPageProps> = ({
  mode = "create",
  onStyleSubmit,
  onCancel,
  useApiClient,
  showNotification,
}) => {
  const [t] = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = qs.parse(location?.search, { ignoreQueryPrefix: true });

  // Component local states
  const [styleSource, setStyleSource] = useState<"pdf" | "paste" | "manual">(
    "pdf",
  );
  const [description, setDescription] = useState("");
  // Text pasted as the source; the description (HTML) is made from it.
  const [pasteText, setPasteText] = useState("");
  const [guidelineTab, setGuidelineTab] = useState<
    "summary" | "appearance" | "aroma" | "flavor" | "bitterness" | "mouthfeel"
  >("summary");
  const [submitAction, setSubmitAction] = useState<
    "draft" | "waiting_for_approval" | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraftSubmitting, setIsDraftSubmitting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGuidelinesGenerated, setIsGuidelinesGenerated] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState({
    sec3: false,
    sec4: false,
    sec5: false,
    sec6: false,
    sec7: false,
    sec8: false,
  });

  const isLocked = styleSource !== "manual" && !isGuidelinesGenerated;

  const handleToggleSection = (section: keyof typeof sectionsOpen) => {
    if (isLocked) return;
    setSectionsOpen((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    if (styleSource === "manual" || isGuidelinesGenerated) {
      setSectionsOpen({
        sec3: true,
        sec4: true,
        sec5: true,
        sec6: true,
        sec7: true,
        sec8: true,
      });
    } else {
      setSectionsOpen({
        sec3: false,
        sec4: false,
        sec5: false,
        sec6: false,
        sec7: false,
        sec8: false,
      });
    }
  }, [styleSource, isGuidelinesGenerated]);

  const methods = useForm<StyleFormData>({
    defaultValues: {
      name: "",
      description: "",
      drinkType: "beer",
      scoresheetSchemaId: "",
      imageUrl: "",
      baseStyle: "Pale Ale",
      version: "1.0",
      minColor: "",
      maxColor: "",
      minOg: "",
      maxOg: "",
      minFg: "",
      maxFg: "",
      minAbv: "",
      maxAbv: "",
      minIbu: "",
      maxIbu: "",
      minServingTemp: "",
      maxServingTemp: "",
      profile: readProfile(null),
      documentPath: "",
      documentName: "",
      guidelines: {},
      settings: {
        entryAdditionalFields: [],
      },
    },
  });

  const {
    watch,
    setValue,
  } = methods;

  const api = useApiClient();

  const createStyleMutation = api.styles.useCreateStyle();
  const updateStyleMutation = api.styles.useUpdateStyle();
  const generateGuidelinesMutation = api.styles.useGenerateGuidelines();

  const documentPath = watch("documentPath") || "";
  const documentName = watch("documentName") || "";
  const guidelines = watch("guidelines") || {};

  const startPollingStyle = (styleId: string) => {
    let attempts = 0;
    const maxAttempts = 150; // 150 attempts * 2 seconds = 5 minutes max timeout
    const pollInterval = 2000; // 2 seconds

    const poll = async () => {
      try {
        const res = await api.styles.findStyle({
          filter: { id: styleId },
          include: "scoresheet_schema",
        });
        const style = res.data;

        if (style?.generationState === "failed") {
          setIsGenerating(false);
          showNotification?.({
            type: "error",
            title: t("Pages:StyleCreator.Notifications.ErrorTitle", "Generation failed"),
            message:
              style.generationError ||
              t("Pages:StyleCreator.Notifications.ErrorMessage", "Something went wrong during generation."),
          });
          return;
        }

        // Done once the job says so; an API without the state falls back to "guidelines exist".
        const generationDone =
          style?.generationState !== undefined
            ? style.generationState === "completed"
            : style?.guidelines && Object.keys(style.guidelines).length > 0;

        if (generationDone && style?.guidelines && Object.keys(style.guidelines).length > 0) {
          const styleDescription = style.description || "";
          setDescription(styleDescription);
          setIsGuidelinesGenerated(true);
          setIsGenerating(false);

          methods.reset({
            name: style.name || watch("name") || "",
            description: styleDescription,
            drinkType: style.drinkType || watch("drinkType") || "beer",
            scoresheetSchemaId:
              style?.scoresheetSchema?.data?.id ||
              style?.scoresheet_schema?.data?.id ||
              style?.scoresheetSchemaId ||
              watch("scoresheetSchemaId") ||
              "",
            image: style.image || "",
            imageUrl: style.image || "",
            documentPath: style.documentPath || watch("documentPath") || "",
            documentName: style.documentName || watch("documentName") || "",
            minColor: style.guidelines?.minColor?.toString() || "",
            maxColor: style.guidelines?.maxColor?.toString() || "",
            minOg: style.guidelines?.minOriginalExtract?.toString() || "",
            maxOg: style.guidelines?.maxOriginalExtract?.toString() || "",
            minFg: style.guidelines?.minFinalExtract?.toString() || "",
            maxFg: style.guidelines?.maxFinalExtract?.toString() || "",
            minAbv: style.guidelines?.minAbv?.toString() || "",
            maxAbv: style.guidelines?.maxAbv?.toString() || "",
            minIbu: style.guidelines?.minIbu?.toString() || "",
            maxIbu: style.guidelines?.maxIbu?.toString() || "",
            minServingTemp: style.minServingTemp?.toString() || watch("minServingTemp") || "",
            maxServingTemp: style.maxServingTemp?.toString() || watch("maxServingTemp") || "",
            profile: readProfile(style.guidelines),
            guidelines: style.guidelines || {},
            settings: {
              entryAdditionalFields: style.entryAdditionalFields || [],
            },
          });

          showNotification?.({
            type: "success",
            title: t("Pages:StyleCreator.Notifications.SuccessTitle", "Guidelines generated!"),
            message: t("Pages:StyleCreator.Notifications.SuccessMessage", "Style guidelines were successfully extracted and filled in the form."),
          });
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setIsGenerating(false);
          showNotification?.({
            type: "error",
            title: t("Pages:StyleCreator.Notifications.TimeoutTitle", "Generation timeout"),
            message: t("Pages:StyleCreator.Notifications.TimeoutMessage", "Guidelines generation is taking longer than expected. Please check back later."),
          });
        }
      } catch (err: any) {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setIsGenerating(false);
          showNotification?.({
            type: "error",
            title: t("Pages:StyleCreator.Notifications.ErrorTitle", "Generation failed"),
            message: err?.errors?.[0]?.detail || err?.message || t("Pages:StyleCreator.Notifications.ErrorMessage", "Something went wrong during generation."),
          });
        }
      }
    };

    setTimeout(poll, pollInterval);
  };

  const triggerGuidelinesGeneration = async (path?: string, name?: string) => {
    const currentPath = path || watch("documentPath");
    const currentName = name || watch("documentName");
    const currentSchemaId = watch("scoresheetSchemaId");

    if (styleSource === "pdf" && !currentPath) {
      showNotification?.({
        type: "error",
        title: t("Pages:StyleCreator.Notifications.NoDocumentTitle", "No document"),
        message: t("Pages:StyleCreator.Notifications.NoDocumentMessage", "Please upload a document first."),
      });
      return;
    }

    if (styleSource === "paste" && !pasteText.trim()) {
      showNotification?.({
        type: "error",
        title: t("Pages:StyleCreator.Notifications.NoDescriptionTitle", "No description"),
        message: t("Pages:StyleCreator.Notifications.NoDescriptionMessage", "Please paste the description first."),
      });
      return;
    }

    if (!currentSchemaId) {
      showNotification?.({
        type: "error",
        title: t("Pages:StyleCreator.Notifications.NoSchemaTitle", "Schema required"),
        message: t("Pages:StyleCreator.Notifications.NoSchemaMessage", "Please select a scoresheet schema in Section 2 first, so AI knows how to align guidelines."),
      });
      return;
    }

    setIsGenerating(true);

    try {
      let activeStyleId = params?.id;

      if (mode === "create" && !activeStyleId) {
        const styleName = watch("name") || (styleSource === "pdf" && currentName ? `Draft ${currentName.split(".")[0]}` : "Draft Style");
        const createPayload = {
          name: styleName,
          description: styleSource === "paste" ? plainTextToHtml(pasteText) : "",
          drinkType: watch("drinkType") || "beer",
          scoresheetSchemaId: currentSchemaId,
          image: watch("imageUrl") || undefined,
          documentPath: styleSource === "pdf" ? currentPath : "",
          documentName: styleSource === "pdf" ? currentName : "",
          state: "draft",
          skip_background_generation: true,
        };

        const res = await createStyleMutation.mutateAsync(createPayload);
        const createdStyle = res.data;
        activeStyleId = createdStyle.id;

        navigate(`/styles/${activeStyleId}/edit`, { replace: true });
      } else if (activeStyleId) {
        await updateStyleMutation.mutateAsync({
          id: activeStyleId,
          description: styleSource === "paste" ? plainTextToHtml(pasteText) : watch("description") || "",
          image: watch("imageUrl") || undefined,
          documentPath: styleSource === "pdf" ? currentPath : "",
          documentName: styleSource === "pdf" ? currentName : "",
          skip_background_generation: true,
        });
      }

      if (activeStyleId) {
        await generateGuidelinesMutation.mutateAsync({ id: activeStyleId });

        showNotification?.({
          type: "info",
          title: t("Pages:StyleCreator.Notifications.GeneratingTitle", "Generating guidelines"),
          message: t("Pages:StyleCreator.Notifications.GeneratingMessage", "AI is analyzing the description and extracting guidelines in the background. Please wait..."),
        });

        startPollingStyle(activeStyleId);
      }
    } catch (err: any) {
      showNotification?.({
        type: "error",
        title: t("Pages:StyleCreator.Notifications.ErrorTitle", "Generation failed"),
        message: err?.errors?.[0]?.detail || err?.message || t("Pages:StyleCreator.Notifications.ErrorMessage", "Something went wrong during generation."),
      });
      setIsGenerating(false);
    }
  };

  const handleDocumentUpload = (uploadedFiles: any[]) => {
    if (uploadedFiles.length > 0 && uploadedFiles[0].url) {
      const fileUrl = uploadedFiles[0].url;
      const fileName = uploadedFiles[0].name;

      setValue("documentPath", fileUrl, { shouldTouch: true });
      setValue("documentName", fileName, { shouldTouch: true });

      triggerGuidelinesGeneration(fileUrl, fileName);
    }
  };

  // Load existing style data if editing
  const { data: styleData } = api.styles.useFindStyle(
    {
      filter: {
        id: mode === "edit" ? params?.id : queryParams?.baseVersionId,
      },
      include: "scoresheet_schema",
    },
    { enabled: mode === "edit" || Boolean(queryParams?.baseVersionId) },
  );

  useEffect(() => {
    if (styleData) {
      const style = styleData.data;
      const styleDescription = style.description || "";

      setDescription(styleDescription);
      setPasteText(htmlToPlainText(styleDescription));

      if (style.guidelines && Object.keys(style.guidelines).length > 0) {
        setIsGuidelinesGenerated(true);
      } else if (mode === "edit") {
        setIsGuidelinesGenerated(true);
      }

      if (style.documentPath) {
        setStyleSource("pdf");
      } else if (styleDescription) {
        setStyleSource("paste");
      } else {
        setStyleSource("manual");
      }

      methods.reset({
        name: style.name || "",
        description: styleDescription,
        drinkType: style.drinkType || "beer",
        scoresheetSchemaId:
          style?.scoresheetSchema?.data?.id ||
          style?.scoresheet_schema?.data?.id ||
          style?.scoresheet_schema?.data?.id ||
          style?.scoresheetSchemaId ||
          "",
        image: style.image || "",
        imageUrl: style.image || "",
        minColor: style.guidelines?.minColor || "",
        maxColor: style.guidelines?.maxColor || "",
        minOg: style.guidelines?.minOriginalExtract || "",
        maxOg: style.guidelines?.maxOriginalExtract || "",
        minFg: style.guidelines?.minFinalExtract || "",
        maxFg: style.guidelines?.maxFinalExtract || "",
        minAbv: style.guidelines?.minAbv || "",
        maxAbv: style.guidelines?.maxAbv || "",
        minIbu: style.guidelines?.minIbu || "",
        maxIbu: style.guidelines?.maxIbu || "",
        minServingTemp: style.minServingTemp || "",
        maxServingTemp: style.maxServingTemp || "",
        profile: readProfile(style.guidelines),
        documentPath: style.documentPath || "",
        documentName: style.documentName || "",
        guidelines: style.guidelines || {},
        settings: {
          entryAdditionalFields: style.entryAdditionalFields || [],
        },
      });
    }
  }, [styleData, methods, mode]);

  const onSubmit = (values: StyleFormData) => {
    const processedValues = { ...values };

    if (mode === "edit" && params?.id) {
      processedValues["id"] = params.id;
    }
    if (queryParams?.baseVersionId) {
      processedValues["baseVersionId"] = queryParams?.baseVersionId;
    }
    if (queryParams?.contests_edition_id) {
      processedValues["contests_edition_id"] = queryParams?.contests_edition_id;
    }

    const stateToApply = submitAction ?? "waiting_for_approval";
    processedValues["state"] = stateToApply;

    processedValues["documentPath"] = values.documentPath || undefined;
    processedValues["documentName"] = values.documentName || undefined;

    processedValues["guidelines"] = buildGuidelinesPayload(values, guidelines);
    processedValues["image"] = values.imageUrl || null;

    processedValues["entryAdditionalFields"] =
      values.settings?.entryAdditionalFields || [];

    if (stateToApply === "waiting_for_approval") {
      setIsSubmitting(true);
    }
    if (stateToApply === "draft") {
      setIsDraftSubmitting(true);
    }
    onStyleSubmit(processedValues);
    setSubmitAction(null);
  };

  // The style as it would be saved right now, for the preview.
  const getPreviewStyle = () => {
    const values = methods.getValues();
    return {
      name: values.name,
      image: values.imageUrl || values.image,
      description: values.description,
      drinkType: values.drinkType,
      minServingTemp: values.minServingTemp,
      maxServingTemp: values.maxServingTemp,
      guidelines: buildGuidelinesPayload(values, values.guidelines),
    };
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      window.close();
      setTimeout(() => {
        navigate(-1);
      }, 100);
    }
  };

  const handleImageUpload = (files: any[]) => {
    if (files.length > 0 && files[0].url) {
      methods.setValue("imageUrl", files[0].url, { shouldTouch: true });
    }
  };

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setDescription(value);
      methods.setValue("description", value, { shouldTouch: true });
    },
    [methods],
  );

  return (
    <div className="min-h-screen pb-24 font-sans text-[#221a14]">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="mx-auto w-full max-w-7xl px-gutter py-8 space-y-6"
        >
          {/* Header with back breadcrumb and title */}
          <header className="py-4">
            <Button
              htmlType="button"
              type="base"
              variant="ghost"
              onClick={handleCancel}
              className="flex items-center gap-2 text-[#6c2f00] hover:underline mb-4 text-sm font-medium p-0"
            >
              <span className="icon-[ph--arrow-left] text-sm" />
              {t("Pages:StyleCreator.Header.Back", "Back to styles")}
            </Button>
            <div className="flex items-center gap-4">
              {(watch("imageUrl") || watch("image")) && (
                <img
                  src={watch("imageUrl") || watch("image")}
                  alt="Style"
                  className="w-16 h-16 rounded-xl object-cover border border-[#dac2b6] shadow-sm flex-shrink-0"
                />
              )}
              <div>
                <h1 className="text-3xl font-bold font-header text-[#6c2f00]">
                  {mode === "edit"
                    ? t("Pages:StyleCreator.Header.EditTitle", "Edit style")
                    : t(
                      "Pages:StyleCreator.Header.CreateTitle",
                      "Create new style",
                    )}
                </h1>
                <p className="text-[#54433a] text-sm mt-1">
                  {t(
                    "Pages:StyleCreator.Header.Subtitle",
                    "Create a new beer style manually or import it from a document.",
                  )}
                </p>
              </div>
            </div>
          </header>

          {/* Main Layout Grid */}
          <div className="grid grid-cols-12 gap-6">
            {/* Left Column: Form Panels */}
            <div className="col-span-12 lg:col-span-9 space-y-6">
              {/* 1. Style Source */}
              <SourceSection
                styleSource={styleSource}
                setStyleSource={setStyleSource}
                description={description}
                pasteText={pasteText}
                setPasteText={setPasteText}
                isGuidelinesGenerated={isGuidelinesGenerated}
                isGenerating={isGenerating}
                triggerGuidelinesGeneration={triggerGuidelinesGeneration}
                handleDocumentUpload={handleDocumentUpload}
                useApiClient={useApiClient}
              />



              {/* 3. Public Description */}
              <DescriptionSection
                isLocked={isLocked}
                isOpen={sectionsOpen.sec3}
                onToggle={() => handleToggleSection("sec3")}
                description={description}
                handleDescriptionChange={handleDescriptionChange}
                triggerGuidelinesGeneration={triggerGuidelinesGeneration}
                isGenerating={isGenerating}
                documentPath={documentPath}
              />

              {/* 4. Style Guidelines */}
              <GuidelinesSection
                isLocked={isLocked}
                isOpen={sectionsOpen.sec4}
                onToggle={() => handleToggleSection("sec4")}
                isGuidelinesGenerated={isGuidelinesGenerated}
                guidelineTab={guidelineTab}
                setGuidelineTab={setGuidelineTab}
              />

              {/* 5. Technical Parameters */}
              <ParamsSection
                isLocked={isLocked}
                isOpen={sectionsOpen.sec5}
                onToggle={() => handleToggleSection("sec5")}
                isGuidelinesGenerated={isGuidelinesGenerated}
              />

              {/* 6. Style Profile */}
              <ProfileSection
                isLocked={isLocked}
                isOpen={sectionsOpen.sec6}
                onToggle={() => handleToggleSection("sec6")}
                isGuidelinesGenerated={isGuidelinesGenerated}
              />

              {/* 7. Entry Form Fields Table */}
              <EntryFieldsSection
                isLocked={isLocked}
                isOpen={sectionsOpen.sec7}
                onToggle={() => handleToggleSection("sec7")}
              />

              {/* 8. Preview */}
              <PreviewSection
                isLocked={isLocked}
                isOpen={sectionsOpen.sec8}
                onToggle={() => handleToggleSection("sec8")}
                getPreviewStyle={getPreviewStyle}
                scoresheetSchemaId={watch("scoresheetSchemaId")}
                useApiClient={useApiClient}
              />
            </div>

            {/* Right Column: Sidebar Info Panel */}
            <aside className="col-span-12 lg:col-span-3 space-y-6">
              <SidebarWidgets />
            </aside>
          </div>
        </form>
      </FormProvider>

      {/* Bottom Footer Fixed Action Bar */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-[#dac2b6] h-20 flex items-center z-50 shadow-lg">
        <div className="max-w-7xl mx-auto w-full px-gutter flex justify-between items-center">
          <Button
            onClick={handleCancel}
            type="base"
            variant="outlined"
            disabled={isSubmitting || isDraftSubmitting}
            className="px-6 py-2 border-[#dac2b6] text-[#54433a] hover:bg-[#fff1e9]/50 rounded-lg font-bold"
          >
            {t("global:shared.Cancel", "Cancel")}
          </Button>

          <div className="flex items-center gap-4">
            {!queryParams?.baseVersionId && (
              <Button
                htmlType="button"
                onClick={() => {
                  setSubmitAction("draft");
                  methods.handleSubmit(onSubmit)();
                }}
                type="base"
                variant="ghost"
                disabled={isSubmitting || isDraftSubmitting}
                className="px-6 py-2 text-[#6c2f00] hover:bg-[#ffdcc3]/30 font-bold rounded-lg"
              >
                {isDraftSubmitting
                  ? t("global:shared.SavingDraft", "Saving draft...")
                  : t("global:shared.SaveDraft", "Save draft")}
              </Button>
            )}

            <Button
              type="primary"
              htmlType="button"
              onClick={() => {
                setSubmitAction("waiting_for_approval");
                methods.handleSubmit(onSubmit)();
              }}
              disabled={isSubmitting || isDraftSubmitting}
              className="bg-[#fe932c] hover:bg-[#6c2f00] text-white rounded-lg font-bold flex items-center gap-2 px-8 py-3 border border-[#fe932c] hover:border-[#6c2f00] transition-all duration-200 shadow-md active:scale-95"
            >
              {isSubmitting
                ? t("global:shared.Submitting", "Submitting...")
                : t("global:shared.Save", "Save")}
              <span className="icon-[ph--arrow-right] text-sm" />
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};
